import _uniq from 'lodash/uniq';
import _cloneDeep from 'lodash/cloneDeep';

var _extends = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; };

/*
 * This file is part of the nivo project.
 *
 * Copyright 2016-present, Raphaël Benitte.
 *
 * For the full copyright and license information, please view the LICENSE
 * file that was distributed with this source code.
 */
import React from 'react';

import { sankey as d3Sankey } from 'd3-sankey';
import { sankeyAlignmentFromProp } from '../../../props';
import SvgWrapper from '../SvgWrapper';
import SankeyNodes from './SankeyNodes';
import SankeyLinks from './SankeyLinks';
import SankeyLabels from './SankeyLabels';
import Container from '../Container';
import { SankeyPropTypes } from './props';
import enhance from './enhance';

var getId = function getId(d) {
    return d.id;
};

var Sankey = function Sankey(_ref) {
    var _data = _ref.data,
        align = _ref.align,
        margin = _ref.margin,
        width = _ref.width,
        height = _ref.height,
        outerWidth = _ref.outerWidth,
        outerHeight = _ref.outerHeight,
        nodeOpacity = _ref.nodeOpacity,
        nodeHoverOpacity = _ref.nodeHoverOpacity,
        nodeHoverOthersOpacity = _ref.nodeHoverOthersOpacity,
        nodeWidth = _ref.nodeWidth,
        nodePaddingX = _ref.nodePaddingX,
        nodePaddingY = _ref.nodePaddingY,
        nodeBorderWidth = _ref.nodeBorderWidth,
        getNodeBorderColor = _ref.getNodeBorderColor,
        setCurrentNode = _ref.setCurrentNode,
        currentNode = _ref.currentNode,
        linkOpacity = _ref.linkOpacity,
        linkHoverOpacity = _ref.linkHoverOpacity,
        linkHoverOthersOpacity = _ref.linkHoverOthersOpacity,
        linkContract = _ref.linkContract,
        getLinkColor = _ref.getLinkColor,
        setCurrentLink = _ref.setCurrentLink,
        currentLink = _ref.currentLink,
        enableLabels = _ref.enableLabels,
        getLabel = _ref.getLabel,
        labelPosition = _ref.labelPosition,
        labelPadding = _ref.labelPadding,
        labelOrientation = _ref.labelOrientation,
        getLabelTextColor = _ref.getLabelTextColor,
        theme = _ref.theme,
        getColor = _ref.getColor,
        nodeTooltip = _ref.nodeTooltip,
        linkTooltip = _ref.linkTooltip,
        animate = _ref.animate,
        motionDamping = _ref.motionDamping,
        motionStiffness = _ref.motionStiffness,
        isInteractive = _ref.isInteractive,
        onClick = _ref.onClick,
        tooltipFormat = _ref.tooltipFormat;

    var sankey = d3Sankey().nodeAlign(sankeyAlignmentFromProp(align)).nodeWidth(nodeWidth).nodePadding(nodePaddingY).size([width, height]).nodeId(getId);

    // deep clone is required as the sankey diagram mutates data
    var data = _cloneDeep(_data);
    sankey(data);

    data.nodes.forEach(function (node) {
        node.color = getColor(node);
        node.label = getLabel(node);
        node.x = node.x0 + nodePaddingX;
        node.y = node.y0;
        node.width = Math.max(node.x1 - node.x0 - nodePaddingX * 2, 0);
        node.height = Math.max(node.y1 - node.y0, 0);
    });

    data.links.forEach(function (link) {
        link.color = getLinkColor(link);
    });

    var motionProps = {
        animate: animate,
        motionDamping: motionDamping,
        motionStiffness: motionStiffness
    };

    var isCurrentNode = function isCurrentNode() {
        return false;
    };
    var isCurrentLink = function isCurrentLink() {
        return false;
    };

    if (currentLink) {
        isCurrentNode = function isCurrentNode(_ref2) {
            var id = _ref2.id;
            return id === currentLink.source.id || id === currentLink.target.id;
        };
        isCurrentLink = function isCurrentLink(_ref3) {
            var source = _ref3.source,
                target = _ref3.target;
            return source.id === currentLink.source.id && target.id === currentLink.target.id;
        };
    }

    if (currentNode) {
        var currentNodeIds = [currentNode.id];
        data.links.filter(function (_ref4) {
            var source = _ref4.source,
                target = _ref4.target;
            return source.id === currentNode.id || target.id === currentNode.id;
        }).forEach(function (_ref5) {
            var source = _ref5.source,
                target = _ref5.target;

            currentNodeIds.push(source.id);
            currentNodeIds.push(target.id);
        });

        currentNodeIds = _uniq(currentNodeIds);
        isCurrentNode = function isCurrentNode(_ref6) {
            var id = _ref6.id;
            return currentNodeIds.includes(id);
        };
        isCurrentLink = function isCurrentLink(_ref7) {
            var source = _ref7.source,
                target = _ref7.target;
            return source.id === currentNode.id || target.id === currentNode.id;
        };
    }

    return React.createElement(
        Container,
        { isInteractive: isInteractive, theme: theme },
        function (_ref8) {
            var showTooltip = _ref8.showTooltip,
                hideTooltip = _ref8.hideTooltip;
            return React.createElement(
                SvgWrapper,
                { width: outerWidth, height: outerHeight, margin: margin },
                React.createElement(SankeyLinks, _extends({
                    links: data.links,
                    linkContract: linkContract,
                    linkOpacity: linkOpacity,
                    linkHoverOpacity: linkHoverOpacity,
                    linkHoverOthersOpacity: linkHoverOthersOpacity,
                    showTooltip: showTooltip,
                    hideTooltip: hideTooltip,
                    setCurrentLink: setCurrentLink,
                    currentNode: currentNode,
                    currentLink: currentLink,
                    isCurrentLink: isCurrentLink,
                    onClick: onClick,
                    tooltip: linkTooltip,
                    theme: theme,
                    tooltipFormat: tooltipFormat
                }, motionProps)),
                React.createElement(SankeyNodes, _extends({
                    nodes: data.nodes,
                    nodePaddingX: nodePaddingX,
                    nodeOpacity: nodeOpacity,
                    nodeHoverOpacity: nodeHoverOpacity,
                    nodeHoverOthersOpacity: nodeHoverOthersOpacity,
                    nodeBorderWidth: nodeBorderWidth,
                    getNodeBorderColor: getNodeBorderColor,
                    showTooltip: showTooltip,
                    hideTooltip: hideTooltip,
                    setCurrentNode: setCurrentNode,
                    currentNode: currentNode,
                    currentLink: currentLink,
                    isCurrentNode: isCurrentNode,
                    onClick: onClick,
                    tooltip: nodeTooltip,
                    theme: theme,
                    tooltipFormat: tooltipFormat
                }, motionProps)),
                enableLabels && React.createElement(SankeyLabels, _extends({
                    nodes: data.nodes,
                    width: width,
                    labelPosition: labelPosition,
                    labelPadding: labelPadding,
                    labelOrientation: labelOrientation,
                    getLabelTextColor: getLabelTextColor,
                    theme: theme
                }, motionProps))
            );
        }
    );
};

Sankey.propTypes = SankeyPropTypes;

export default enhance(Sankey);


//////////////////
// WEBPACK FOOTER
// ./node_modules/nivo/es/components/charts/sankey/Sankey.js
// module id = 544
// module chunks = 0